"use client";

import { Review } from "@/types/Review";
import React, { useEffect, useState } from "react";
import LeftArrow from "../icons/LeftArrow";
import RightArrow from "../icons/RightArrow";
import Star from "../icons/Star";
import "./index.scss";

function useWindowSize() {
  const [size, setSize] = React.useState<{
    width: number | null;
    height: number | null;
  }>({
    width: null,
    height: null,
  });

  React.useLayoutEffect(() => {
    const handleResize = () => {
      setSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return size;
}

const Reviews = ({
  reviews,
  autoScroll = false,
  numberOfVisibleReviews,
}: {
  reviews: any;
  autoScroll: boolean;
  numberOfVisibleReviews: number;
}) => {
  const isPaginated = reviews.length > numberOfVisibleReviews;

  const [marginLeft, setMarginLeft] = useState(-100);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isAutoScroll, setIsAutoScroll] = useState(autoScroll && isPaginated);
  const size = useWindowSize();

  let normalizedNumberOfVisibleReviews = numberOfVisibleReviews;
  if (size.width) {
    let pixelWidthPerReview = (size.width - 36 - 36) / numberOfVisibleReviews;
    while (pixelWidthPerReview < 436 && normalizedNumberOfVisibleReviews > 1) {
      normalizedNumberOfVisibleReviews = normalizedNumberOfVisibleReviews - 1;
      pixelWidthPerReview =
        (size.width - 36 - 36) / normalizedNumberOfVisibleReviews;
    }
  }

  const numberOfEmptyReviewsToPad =
    Math.ceil(reviews.length / normalizedNumberOfVisibleReviews) *
      normalizedNumberOfVisibleReviews -
    reviews.length;

  const normalizedReviews = [...reviews];
  for (let count = 0; count < numberOfEmptyReviewsToPad; count++) {
    normalizedReviews.push({
      author: "**EMPTY**",
      title: "**EMPTY**",
      content: "**EMPTY**",
    });
  }

  useEffect(() => {
    if (isAutoScroll) {
      const interval = setInterval(() => {
        handleNext();
      }, 6000);

      return () => clearInterval(interval);
    }
  }, [marginLeft, isAutoScroll]);

  const isFirstPage = marginLeft === -100;
  const isLastPage =
    marginLeft ===
    -100 * (normalizedReviews.length / normalizedNumberOfVisibleReviews);

  const handlePrevious = () => {
    setIsAutoScroll(false);
    setIsTransitioning(true);
    setMarginLeft(marginLeft + 100);
    if (isFirstPage) {
      setTimeout(() => {
        // Reset scroll position
        setMarginLeft(
          -100 * (normalizedReviews.length / normalizedNumberOfVisibleReviews)
        );
      }, 2000);
    }

    setTimeout(() => {
      setIsTransitioning(false);
    }, 2040);
  };

  const handleNext = () => {
    setIsAutoScroll(false);
    setIsTransitioning(true);
    setMarginLeft(marginLeft - 100);
    if (isLastPage) {
      setTimeout(() => {
        // Reset scroll position
        setMarginLeft(-100);
      }, 2000);
    }

    setTimeout(() => {
      setIsTransitioning(false);
    }, 2040);
  };

  const createReviewNodes = (
    reviews: Review[],
    numberOfReviews: number,
    isAriaHidden: boolean,
    specialKey = ""
  ) => {
    return reviews.map((review, reviewIndex) => {
      if (
        review.author === "**EMPTY**" &&
        review.title === "**EMPTY**" &&
        review.content === "**EMPTY**"
      ) {
        return (
          <li
            key={`empty-${reviewIndex}`}
            className="review"
            style={{
              width: `${100 / numberOfReviews}%`,
            }}
            aria-hidden={true}
          >
            &nbsp;
          </li>
        );
      }
      return (
        <li
          key={`${review.author}-${reviewIndex}-${specialKey}`}
          className="review"
          style={{
            width: `${100 / numberOfReviews}%`,
          }}
          aria-hidden={isAriaHidden}
        >
          <div className="review-inner-wrapper">
            <div className="rating" aria-label="5 out of 5 stars" role="img">
              <Star />
              <Star />
              <Star />
              <Star />
              <Star />
            </div>
            {review.author && review.author.length > 0 && (
              <div className="author">{review.author}</div>
            )}
            {review.title && review.title.length > 0 && (
              <div className="review-title">{review.title}</div>
            )}
            <div className="content">{review.content}</div>
          </div>
        </li>
      );
    });
  };

  const numberOfReviews =
    normalizedReviews.length + 2 * normalizedNumberOfVisibleReviews;
  const reviewNodes = [
    ...createReviewNodes(
      normalizedReviews.slice(normalizedNumberOfVisibleReviews * -1),
      numberOfReviews,
      true,
      "hidden1"
    ),
    ...createReviewNodes(normalizedReviews, numberOfReviews, false),
    ...createReviewNodes(
      normalizedReviews.slice(0, normalizedNumberOfVisibleReviews),
      numberOfReviews,
      true,
      "hidden2"
    ),
  ];

  const listStyles: any = {
    width: `${(100 / normalizedNumberOfVisibleReviews) * numberOfReviews}%`,
    marginLeft: `${marginLeft}%`,
  };

  if (isTransitioning) {
    listStyles["transitionDuration"] = "2s";
  }

  return (
    <div className="component reviews">
      {isPaginated && (
        <button
          className="previous"
          type="button"
          onClick={handlePrevious}
          disabled={isTransitioning}
          aria-label="Previous"
        >
          <LeftArrow />
        </button>
      )}
      {isPaginated && (
        <button
          className="next"
          type="button"
          onClick={handleNext}
          disabled={isTransitioning}
          aria-label="Next"
        >
          <RightArrow />
        </button>
      )}

      <div className="review-list-outer-wrapper">
        <ul style={listStyles}>{reviewNodes}</ul>
      </div>
    </div>
  );
};

export default Reviews;
